import { IBAN_COUNTRIES, LOCAL_CODE_BY_COUNTRY, PIX_KEY_TYPES } from '@loot/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.ts';
import { SearchSelect } from '../../components/SearchSelect.tsx';
import { Button, ErrorBox, Field, Input, Loading, Select, Textarea } from '../../components/ui.tsx';
import { CountrySelect } from './common.tsx';
import { useFormat } from '../purchasing/common.tsx';

/**
 * Peças do formulário de conta bancária, as mesmas para fornecedores e para as empresas do grupo
 * (ADR 0073, revisão de 01/11/2026): cada tela guarda as contas na sua tabela, mas pede tudo do
 * mesmo jeito, com a mesma lista de bancos, de moedas e de países.
 */

interface BankList {
  banks: { ispb: string; code: string; shortName: string; fullName: string }[];
  lastSync: {
    status: 'running' | 'ok' | 'failed' | null;
    startedAt: string | null;
    error: string | null;
    lastOkAt: string | null;
  };
  canSync: boolean;
}

const bankLabel = (b: { code: string; shortName: string }) => `${b.code} · ${b.shortName}`;

/** Nacional, internacional (e PIX, no fornecedor): a primeira escolha, sempre à vista. */
export function AccountChoice<K extends string>({
  value,
  options,
  onChange,
}: {
  value: K;
  options: readonly K[];
  onChange: (next: K) => void;
}) {
  const { t } = useTranslation();
  const label = t('partners.bank.choice');
  return (
    <div className="flex flex-col gap-1 text-sm sm:col-span-2">
      <span className="font-medium">{label}</span>
      <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
        {options.map((o) => (
          <Button
            key={o}
            role="radio"
            aria-checked={value === o}
            variant={value === o ? undefined : 'secondary'}
            onClick={() => onChange(o)}
          >
            {t(`partners.bank.choices.${o}`)}
          </Button>
        ))}
      </div>
    </div>
  );
}

/**
 * Escolha do banco pela lista oficial do Banco Central, com a busca aberta ao clicar: só vale um
 * banco da lista. Mostra a data da última atualização, o aviso se a última falhou e o botão
 * "Atualizar agora". Sem a lista carregada, vale o código digitado.
 */
export function BankPicker({
  value,
  onChange,
}: {
  value: { bankIspb: string; bankCode: string; bankName: string };
  onChange: (v: { bankIspb: string; bankCode: string; bankName: string }) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { dateTime } = useFormat();
  const list = useQuery({ queryKey: ['banks'], queryFn: () => api.get<BankList>('/banks') });
  const sync = useMutation({
    mutationFn: () => api.post('/banks/sync', {}),
    onSettled: () => qc.invalidateQueries({ queryKey: ['banks'] }),
  });
  const banks = useMemo(() => list.data?.banks ?? [], [list.data]);
  const options = useMemo(
    () =>
      banks.map((b) => ({
        id: b.ispb,
        label: bankLabel(b),
        detail: b.fullName === b.shortName ? undefined : b.fullName,
      })),
    [banks],
  );
  if (list.isLoading) return <Loading />;
  const data = list.data;
  const status = data?.lastSync;
  // Conta anterior à lista só tem o código: acha o banco por ele.
  const current = banks.find((b) =>
    value.bankIspb ? b.ispb === value.bankIspb : b.code === value.bankCode,
  );
  return (
    <div className="flex flex-col gap-1 sm:col-span-2">
      {banks.length > 0 ? (
        <Field label={t('partners.bank.fields.bank')} required>
          <SearchSelect
            required
            value={current?.ispb ?? ''}
            options={options}
            placeholder={t('partners.bank.bankSearch')}
            onChange={(ispb) => {
              const found = banks.find((b) => b.ispb === ispb);
              if (found) {
                onChange({ bankIspb: found.ispb, bankCode: found.code, bankName: found.shortName });
              }
            }}
          />
        </Field>
      ) : (
        <>
          <p className="text-sm text-muted">{t('partners.bank.bankListEmpty')}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('partners.bank.fields.bankCode')} required>
              <Input
                id="bank-bankCode"
                required
                value={value.bankCode}
                onChange={(e) => onChange({ ...value, bankIspb: '', bankCode: e.target.value })}
              />
            </Field>
            <Field label={t('partners.bank.fields.bankName')}>
              <Input
                id="bank-bankName"
                value={value.bankName}
                onChange={(e) => onChange({ ...value, bankName: e.target.value })}
              />
            </Field>
          </div>
        </>
      )}
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted">
        {status?.lastOkAt && (
          <span>{t('partners.bank.bankListUpdated', { date: dateTime(status.lastOkAt) })}</span>
        )}
        {status?.status === 'failed' && (
          <span role="alert" className="text-danger">
            {t('partners.bank.bankListFailed')}
          </span>
        )}
        {data?.canSync && (
          <Button
            type="button"
            variant="secondary"
            disabled={sync.isPending || status?.status === 'running'}
            onClick={() => sync.mutate()}
          >
            {sync.isPending ? t('partners.bank.syncing') : t('partners.bank.syncNow')}
          </Button>
        )}
      </div>
      {sync.error && <ErrorBox error={sync.error} />}
    </div>
  );
}

/** Caixa "Incluir também uma chave PIX desta conta". */
export function PixToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <label className="flex items-center gap-2 text-sm font-medium sm:col-span-2">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {t('partners.bank.addPix')}
    </label>
  );
}

/** Tipo e chave PIX. */
export function PixFields({
  type,
  value,
  onChange,
  children,
}: {
  type: string;
  value: string;
  onChange: (patch: { pixKeyType?: string; pixKey?: string }) => void;
  /** Aviso abaixo dos campos (ex.: a chave é de outra pessoa). */
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Field label={t('partners.bank.fields.pixKeyType')} required>
        <Select
          id="bank-pixKeyType"
          value={type}
          onChange={(e) => onChange({ pixKeyType: e.target.value })}
        >
          {PIX_KEY_TYPES.map((k) => (
            <option key={k} value={k}>
              {t(`partners.bank.pixTypes.${k}`)}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('partners.bank.fields.pixKey')} required>
        <Input
          id="bank-pixKey"
          required
          value={value}
          onChange={(e) => onChange({ pixKey: e.target.value })}
        />
      </Field>
      {children}
    </>
  );
}

export interface AbroadValues {
  country: string;
  intlFormat: string;
  iban: string;
  accountNumber: string;
  localCode: string;
  swiftBic: string;
  bankName: string;
  bankAddress: string;
  withIntermediary: string;
  intermediarySwift: string;
  intermediaryName: string;
  intermediaryAccount: string;
}

/**
 * Conta no exterior: país, formato (IBAN ou número e código local), SWIFT, banco e, se houver, o
 * banco intermediário. O SWIFT e o endereço não são exigidos em toda conta da empresa.
 */
export function AbroadFields({
  f,
  onChange,
  swiftRequired = true,
  addressRequired = true,
}: {
  f: AbroadValues;
  onChange: (patch: Partial<AbroadValues>) => void;
  swiftRequired?: boolean;
  addressRequired?: boolean;
}) {
  const { t } = useTranslation();
  const localType = LOCAL_CODE_BY_COUNTRY[f.country];
  const field = (k: keyof AbroadValues, required = true) => (
    <Field label={t(`partners.bank.fields.${k}`)} required={required}>
      <Input
        id={`bank-${k}`}
        required={required}
        value={f[k]}
        onChange={(e) => onChange({ [k]: e.target.value })}
      />
    </Field>
  );
  return (
    <>
      <Field label={t('partners.bank.fields.country')} required>
        <CountrySelect
          id="bank-country"
          value={f.country}
          exclude={['BR']}
          onChange={(country) =>
            onChange({ country, intlFormat: IBAN_COUNTRIES.has(country) ? 'iban' : 'account' })
          }
        />
      </Field>
      <Field label={t('partners.bank.fields.intlFormat')} required>
        <Select
          id="bank-intlFormat"
          value={f.intlFormat}
          onChange={(e) => onChange({ intlFormat: e.target.value })}
        >
          <option value="iban">{t('partners.bank.intlFormats.iban')}</option>
          <option value="account">{t('partners.bank.intlFormats.account')}</option>
        </Select>
      </Field>
      {f.intlFormat === 'iban' ? (
        field('iban')
      ) : (
        <>
          {field('accountNumber')}
          {localType && (
            <Field label={t(`partners.bank.localCodes.${localType}`)} required>
              <Input
                id="bank-localCode"
                required
                value={f.localCode}
                onChange={(e) => onChange({ localCode: e.target.value })}
              />
            </Field>
          )}
        </>
      )}
      {f.intlFormat === 'account' && f.country === 'MX' && (
        <p className="text-xs text-muted sm:col-span-2">{t('partners.bank.clabeHint')}</p>
      )}
      {f.intlFormat === 'account' && IBAN_COUNTRIES.has(f.country) && f.country !== 'GB' && (
        <p role="status" className="text-sm text-danger sm:col-span-2">
          {t('partners.bank.ibanRequired')}
        </p>
      )}
      {field('swiftBic', swiftRequired)}
      {field('bankName')}
      <Field
        label={t('partners.bank.fields.bankAddress')}
        className="sm:col-span-2"
        required={addressRequired}
      >
        <Textarea
          id="bank-bankAddress"
          required={addressRequired}
          maxLength={300}
          value={f.bankAddress}
          onChange={(e) => onChange({ bankAddress: e.target.value })}
        />
      </Field>
      <label className="flex items-center gap-2 text-sm sm:col-span-2">
        <input
          type="checkbox"
          checked={!!f.withIntermediary}
          onChange={(e) => onChange({ withIntermediary: e.target.checked ? 'yes' : '' })}
        />
        {t('partners.bank.addIntermediary')}
      </label>
      {f.withIntermediary && (
        <>
          {field('intermediarySwift')}
          {field('intermediaryName')}
          {field('intermediaryAccount', false)}
        </>
      )}
    </>
  );
}
